/**
 * Career Break Calculator — deterministic model.
 *
 * Ported 1:1 from the marketing-provided prototype (`Career Break Calculator v2.html`,
 * `window.__cbcCalc`). Every constant, branch and rounding rule below mirrors that
 * prototype exactly; the unit tests pin the prototype's own default-input outputs.
 * Do not "improve" the math or the copy here without checking back with marketing —
 * this is a faithful port, not a redesign.
 *
 * Tax year is 2026: brackets and standard deductions from IRS Rev. Proc. 2025-32,
 * Social Security wage base $184,500, 401(k) limits including the age 50+ and age
 * 60–63 catch-ups. COBRA uses KFF 2025 employer premium averages. Marketplace
 * pricing uses the 2025 federal poverty guidelines (the ones that apply to the 2026
 * plan year) and assumes the enhanced premium tax credits expired on 2025-12-31, so
 * the 400% cliff applies.
 */

import type { CareerBreakResults, CareerBreakState, Coverage, FilingStatus, Lever, Verdict } from '../types'
import { months, usd0 } from './format'

const STD_DEDUCTION: Record<FilingStatus, number> = { single: 16_100, mfj: 32_200, hoh: 24_150, mfs: 16_100 }

/** [lower bound of bracket, marginal rate]. */
const BRACKETS: Record<FilingStatus, [number, number][]> = {
  single: [
    [0, 0.1],
    [12_400, 0.12],
    [50_400, 0.22],
    [105_700, 0.24],
    [201_775, 0.32],
    [256_225, 0.35],
    [640_600, 0.37],
  ],
  mfj: [
    [0, 0.1],
    [24_800, 0.12],
    [100_800, 0.22],
    [211_400, 0.24],
    [403_550, 0.32],
    [512_450, 0.35],
    [768_700, 0.37],
  ],
  hoh: [
    [0, 0.1],
    [17_700, 0.12],
    [67_450, 0.22],
    [105_700, 0.24],
    [201_775, 0.32],
    [256_200, 0.35],
    [640_600, 0.37],
  ],
  mfs: [
    [0, 0.1],
    [12_400, 0.12],
    [50_400, 0.22],
    [105_700, 0.24],
    [201_775, 0.32],
    [256_225, 0.35],
    [384_350, 0.37],
  ],
}

/** Wage threshold above which the 0.9% Additional Medicare Tax applies. */
const ADDITIONAL_MEDICARE_THRESHOLD: Record<FilingStatus, number> = {
  single: 200_000,
  mfj: 250_000,
  hoh: 200_000,
  mfs: 125_000,
}

const DEFERRAL = { base: 24_500, catchUp50: 8_000, catchUp60: 11_250 }

function deferralLimit(age: number): number {
  if (age >= 60 && age <= 63) return DEFERRAL.base + DEFERRAL.catchUp60
  if (age >= 50) return DEFERRAL.base + DEFERRAL.catchUp50
  return DEFERRAL.base
}
const SS_WAGE_BASE = 184_500

/** 2025 federal poverty guidelines by household size, used for 2026 plan-year subsidies. */
const FPL_BY_HOUSEHOLD_SIZE: Record<number, number> = {
  1: 15_650,
  2: 21_150,
  3: 27_320,
  4: 32_150,
  5: 37_650,
  6: 43_150,
  7: 48_650,
}

const COBRA = { single: 792.63, family: 2_294.41 }
const COBRA_MAX_MONTHS = 18
/** National average benchmark marketplace premium for a 40-year-old. */
const ACA_BENCHMARK_AGE_40 = 625

const MARKETPLACE_VARIANCE_NOTE =
  ' This is a national average adjusted for age and household size. Real premiums vary by state and rating area — get a quote if you want a more precise estimate.'

export const DEFAULT_STATE: CareerBreakState = {
  age: 38,
  household: 'couple',
  income: 165_000,
  partnerIncome: 120_000,
  partnerWorks: 'yes',
  breakMonths: 12,
  householdSize: 3,

  liquid: 85_000,
  retirementBalance: 425_000,
  monthlySpend: 12_500,

  contributionPct: 10,
  matchTenths: 40,
  currentPremium: 571,
  coverage: 'partner',
  partnerPlanCost: 450,

  careSavings: 1_100,
  otherSavings: 400,

  retirementAge: 65,
  returnPct: 7,
  careerStartAge: 22,
  returnPayPct: 90,
  yearsToFullPay: 3,
  filingSelection: 'auto',
  stateTaxPct: 5,
  reserveMonths: 3,
  endingCushion: 0,
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const lerp = (x: number, x0: number, x1: number, y0: number, y1: number) => y0 + ((x - x0) * (y1 - y0)) / (x1 - x0)

/**
 * Share of income a household is expected to pay for the benchmark plan, by percentage
 * of the federal poverty line. `null` above 400% — the subsidy cliff, which is back in
 * force for the 2026 plan year.
 */
export const applicablePercentage = (pctOfPoverty: number): number | null => {
  if (pctOfPoverty < 133) return 0.021
  if (pctOfPoverty < 150) return lerp(pctOfPoverty, 133, 150, 0.0314, 0.0419)
  if (pctOfPoverty < 200) return lerp(pctOfPoverty, 150, 200, 0.0419, 0.066)
  if (pctOfPoverty < 250) return lerp(pctOfPoverty, 200, 250, 0.066, 0.0844)
  if (pctOfPoverty < 300) return lerp(pctOfPoverty, 250, 300, 0.0844, 0.0996)
  if (pctOfPoverty <= 400) return 0.0996
  return null
}

/** Premium age-rating curve, normalised so a 40-year-old is 1.0. */
export const ageFactor = (age: number): number => {
  const curve = (x: number) => (x <= 20 ? 1 : 1 + ((Math.min(x, 64) - 20) * (3 - 1)) / 44)
  return curve(age) / curve(40)
}

export function federalTax(taxableIncome: number, filing: FilingStatus): number {
  const taxable = Math.max(0, taxableIncome)
  const brackets = BRACKETS[filing]
  let tax = 0
  for (const [i, [lower, rate]] of brackets.entries()) {
    const upper = brackets[i + 1]?.[0] ?? Infinity
    if (taxable > lower) tax += (Math.min(taxable, upper) - lower) * rate
  }
  return tax
}

const fica = (wages: number) => Math.min(wages, SS_WAGE_BASE) * 0.062 + wages * 0.0145

const additionalMedicare = (wages: number, filing: FilingStatus) =>
  Math.max(0, wages - ADDITIONAL_MEDICARE_THRESHOLD[filing]) * 0.009

/** Annual take-home and total tax for a household, given a pre-tax 401(k) deferral. */
export function netCash(
  wages: number,
  partnerWages: number,
  deferral: number,
  filing: FilingStatus,
  stateRate: number
): { net: number; tax: number } {
  const gross = wages + partnerWages
  const taxable = Math.max(0, gross - deferral - STD_DEDUCTION[filing])
  const federal = federalTax(taxable, filing)
  const state = stateRate * taxable
  const payroll = fica(wages) + fica(partnerWages) + additionalMedicare(gross, filing)
  return { net: gross - deferral - federal - state - payroll, tax: federal + state + payroll }
}

/**
 * Future value of a monthly payment stream that runs for `n` months, then sits invested
 * for a further `growthMonths`.
 */
export function futureValueOfStream(payment: number, n: number, growthMonths: number, monthlyRate: number): number {
  if (monthlyRate === 0) return payment * n
  const atEndOfStream = payment * ((Math.pow(1 + monthlyRate, n) - 1) / monthlyRate)
  return atEndOfStream * Math.pow(1 + monthlyRate, Math.max(0, growthMonths))
}

export function computeResults(state: CareerBreakState): CareerBreakResults {
  const {
    age,
    household,
    income,
    partnerWorks,
    breakMonths: L,
    liquid,
    retirementBalance,
    monthlySpend: spend,
    currentPremium,
    coverage: coverageSelection,
    partnerPlanCost,
    careSavings,
    otherSavings,
    retirementAge,
    careerStartAge,
    yearsToFullPay: recover,
    endingCushion: cushion,
  } = state

  // A partner's plan is only priceable if there is a partner still on one. The panel hides
  // the option when that stops being true, but the stored choice survives the switch, so the
  // model normalises it here — the server recomputes from posted state and must agree.
  const partnerPlanAvailable = household === 'couple' && partnerWorks === 'yes'
  const coverageChoice: Coverage =
    coverageSelection === 'partner' && !partnerPlanAvailable ? 'cobra' : coverageSelection

  const filing: FilingStatus =
    state.filingSelection === 'auto' ? (household === 'couple' ? 'mfj' : 'single') : state.filingSelection
  const partnerIncome = household === 'couple' ? state.partnerIncome : 0
  /** Partner income that continues during the break — zero if they step away too. */
  const partnerIncomeDuringBreak = household === 'couple' && partnerWorks === 'yes' ? partnerIncome : 0
  const householdSize = clamp(state.householdSize, 1, 7)
  const contributionRate = state.contributionPct / 100
  const matchRate = state.matchTenths / 1000
  const returnRate = state.returnPct / 100
  const stateRate = state.stateTaxPct / 100
  const reserveMonths = state.reserveMonths
  const returnPayRate = state.returnPayPct / 100
  const monthlyRate = Math.pow(1 + returnRate, 1 / 12) - 1
  const breakYears = L / 12

  const deferralLimitForAge = deferralLimit(age)
  const annualDeferral = income * contributionRate
  /** You only capture match up to what you actually contribute. */
  const effectiveMatchRate = Math.min(matchRate, contributionRate)
  const annualMatch = income * effectiveMatchRate

  const priceMarketplace = (): { premium: number; note: string } => {
    const fpl = FPL_BY_HOUSEHOLD_SIZE[householdSize] ?? 0
    const magi = partnerIncomeDuringBreak
    const pctOfPoverty = fpl > 0 ? (magi / fpl) * 100 : 999
    const adults = household === 'couple' ? 2 : 1
    const kids = Math.max(0, householdSize - adults)
    const grossPremium =
      ACA_BENCHMARK_AGE_40 * ageFactor(age) * (adults > 1 ? 1.9 : 1) + kids * ACA_BENCHMARK_AGE_40 * 0.55
    const applicable = applicablePercentage(pctOfPoverty)

    if (pctOfPoverty < 138) {
      return {
        premium: grossPremium,
        note:
          `Household income during the break is ${Math.round(pctOfPoverty)}% of the poverty line. Depending on your state you may qualify for Medicaid at little or no cost, or fall into the coverage gap — we can’t price that without knowing your state, so this shows full price. Confirm before relying on it.` +
          MARKETPLACE_VARIANCE_NOTE,
      }
    }
    if (applicable === null) {
      return {
        premium: grossPremium,
        note:
          `At ${Math.round(pctOfPoverty)}% of the poverty line you’re over the 400% cliff, and the enhanced subsidies expired at the end of 2025 — so no tax credit in 2026. Full price for your household is roughly ${usd0(grossPremium)}/mo.` +
          MARKETPLACE_VARIANCE_NOTE,
      }
    }
    const netPremium = Math.min(grossPremium, (magi * applicable) / 12)
    return {
      premium: netPremium,
      note:
        `Your income drops to ${Math.round(pctOfPoverty)}% of the poverty line during the break, which unlocks a premium tax credit: roughly ${usd0(netPremium)}/mo instead of ${usd0(grossPremium)}. If anyone in the household is offered affordable employer coverage, you may not be eligible.` +
        MARKETPLACE_VARIANCE_NOTE,
    }
  }

  let newPremium = 0
  let coverageNote = ''
  if (coverageChoice === 'partner') {
    newPremium = partnerPlanCost
    coverageNote = 'Usually the cheapest option, if their plan allows a mid-year add for a qualifying life event.'
  } else if (coverageChoice === 'cobra') {
    const cobra = householdSize > 1 ? COBRA.family : COBRA.single
    if (L <= COBRA_MAX_MONTHS) {
      newPremium = cobra
      coverageNote = `COBRA is the full premium plus a 2% administrative fee — about ${usd0(cobra)}/mo at national averages. It runs out after ${COBRA_MAX_MONTHS} months.`
    } else {
      const marketplace = priceMarketplace()
      const monthsAfterCobra = L - COBRA_MAX_MONTHS
      newPremium = (cobra * COBRA_MAX_MONTHS + marketplace.premium * monthsAfterCobra) / L
      coverageNote =
        `COBRA runs out after ${COBRA_MAX_MONTHS} months, so we price ${COBRA_MAX_MONTHS} months at about ${usd0(cobra)}/mo and the remaining ${monthsAfterCobra} months on a marketplace plan at about ${usd0(marketplace.premium)}/mo — an average of ${usd0(newPremium)}/mo across the break.` +
        MARKETPLACE_VARIANCE_NOTE
    }
  } else {
    const marketplace = priceMarketplace()
    newPremium = marketplace.premium
    coverageNote = marketplace.note
  }
  const healthDelta = newPremium - currentPremium

  const cappedDeferral = Math.min(annualDeferral, deferralLimitForAge)
  const now = netCash(income, partnerIncome, cappedDeferral, filing, stateRate)
  const duringBreak = netCash(0, partnerIncomeDuringBreak, 0, filing, stateRate)

  /** Spending that stops can never exceed what the household spends in the first place. */
  const savingsMonthly = Math.min(careSavings + otherSavings, spend)
  const breakNetMonthly = duringBreak.net / 12
  const needMonthly = spend - savingsMonthly + healthDelta
  const signedGapMonthly = needMonthly - breakNetMonthly
  const gapMonthly = Math.max(0, signedGapMonthly)
  const totalNeed = gapMonthly * L
  const reserve = reserveMonths * spend
  const projectedEndCash = liquid - signedGapMonthly * L
  const goalMet = projectedEndCash >= reserve + cushion
  const aboveReserve = Math.max(0, liquid - reserve)
  const available = Math.max(0, aboveReserve - cushion)
  const coverage = totalNeed > 0 ? available / totalNeed : goalMet ? Infinity : 0
  const runwayMonths = gapMonthly > 0 ? aboveReserve / gapMonthly : Infinity
  const canFund = aboveReserve >= totalNeed

  let verdict: Verdict
  let verdictHeadline: string
  let verdictDetail: string
  if (cushion > 0 && !goalMet && canFund) {
    verdict = 'tight'
    verdictHeadline = 'Your numbers cover the break — but not the cushion you asked for.'
    verdictDetail = `The cash you entered covers the break itself — you’d last ${months(runwayMonths)} — but you’d finish with ${usd0(Math.max(0, projectedEndCash - reserve))} spare rather than the ${usd0(cushion)} you asked to keep.`
  } else if (totalNeed <= 0) {
    verdict = 'comfortable'
    verdictHeadline = 'Based on your numbers, your cash flow already covers it.'
    verdictDetail = `With ${usd0(savingsMonthly)}/mo of spending that stops and your household’s remaining income, you don’t need to draw down savings at all.`
  } else if (coverage >= 1.75) {
    verdict = 'comfortable'
    verdictHeadline = 'Your numbers cover it comfortably.'
    verdictDetail = `The cash you entered covers ${months(runwayMonths)} at this burn rate. You want ${months(L)}.`
  } else if (coverage >= 1.25) {
    verdict = 'comfortable'
    verdictHeadline = 'Your numbers cover it, with room to breathe.'
    verdictDetail = `Your cash lasts ${months(runwayMonths)} against a ${months(L)} break — a ${Math.round((coverage - 1) * 100)}% cushion if things run long.`
  } else if (coverage >= 1) {
    verdict = 'workable'
    verdictHeadline = 'Your numbers cover it — but it’s tight.'
    verdictDetail = `You’d finish the break with about ${usd0(available - totalNeed)} left over. One surprise erases that.`
  } else if (coverage >= 0.7) {
    verdict = 'short'
    verdictHeadline = `Your numbers come up ${usd0(totalNeed - available)} short.`
    verdictDetail = `Your money runs out at ${months(runwayMonths)}. A ${Math.floor(runwayMonths)}-month break would fit these numbers.`
  } else {
    verdict = 'short'
    verdictHeadline = 'Your numbers don’t cover it yet.'
    verdictDetail = `At this burn rate your cash covers ${months(runwayMonths)} of a ${months(L)} break. You’d be ${usd0(totalNeed - available)} short.`
  }

  const canTake = (coverage >= 1 && goalMet) || totalNeed <= 0
  const thinMargin =
    totalNeed > 0 && ((cushion > 0 && !goalMet && canFund) || (goalMet && coverage >= 1 && coverage < 1.25))
  const advisorHeadline = thinMargin
    ? 'A thin margin is a plan with no room for error.'
    : canTake
      ? 'Let’s build your plan to step away.'
      : 'Let’s see if you can get there.'

  const monthsToRetirement = Math.max(0, (retirementAge - age) * 12)
  const growthAfterBreak = Math.max(0, monthsToRetirement - L)
  const missedMonthly = cappedDeferral / 12 + annualMatch / 12
  const missedTotal = missedMonthly * L
  const missedFutureValue = futureValueOfStream(
    missedMonthly,
    Math.min(L, monthsToRetirement),
    growthAfterBreak,
    monthlyRate
  )
  const drawdownFutureValue = futureValueOfStream(
    gapMonthly,
    Math.min(L, monthsToRetirement),
    growthAfterBreak,
    monthlyRate
  )
  const longTermCost = missedFutureValue + drawdownFutureValue

  let payGap = 0
  const shortfall = Math.max(0, 1 - returnPayRate)
  if (recover > 0) {
    for (let year = 1; year <= recover; year++) payGap += income * shortfall * (1 - (year - 1) / recover)
  }
  // A raise on return is modelled as a gain rather than a gap.
  if (returnPayRate > 1) payGap = (-income * (returnPayRate - 1) * Math.max(1, recover)) / 2

  const workYearsAfter = Math.max(0, retirementAge - careerStartAge) - breakYears
  // Social Security averages the highest 35 years of indexed earnings.
  const socialSecurityReduced = workYearsAfter < 35
  const socialSecurityNote = socialSecurityReduced
    ? `You’d finish with about ${workYearsAfter.toFixed(0)} working years, under the 35 Social Security averages, so a zero year pulls the average down.`
    : `You’d still have about ${Math.floor(workYearsAfter)} working years against the 35 Social Security averages, so the effect is probably small — though a high-earning year you skip can still displace a lower one.`

  const projectedNoBreak =
    retirementBalance * Math.pow(1 + monthlyRate, monthsToRetirement) +
    futureValueOfStream(missedMonthly, monthsToRetirement, 0, monthlyRate)
  const projectedWithBreak = Math.max(0, projectedNoBreak - missedFutureValue)

  const levers: Lever[] = []
  const shortCash = Math.max(0, totalNeed + cushion - available)
  const monthlySavingRate = now.net / 12 - spend
  if (shortCash > 0) {
    if (Math.floor(runwayMonths) >= 1 && coverage < 1) {
      levers.push({ label: 'Shorten the break to', value: months(Math.floor(runwayMonths)) })
    }
    levers.push({
      label: 'Or save this much beforehand',
      tip:
        cushion > 0
          ? `Covers the ${usd0(totalNeed)} the break costs plus the ${usd0(cushion)} you want left over at the end. Your emergency reserve stays untouched either way.`
          : 'What it takes to break even. You would finish the break with nothing left beyond your emergency reserve — set a cash cushion in Advanced if you want margin on top of that.',
      value: usd0(shortCash),
    })
    if (monthlySavingRate > 0) {
      const monthsOfSaving = shortCash / monthlySavingRate
      if (monthsOfSaving > 0 && monthsOfSaving < 360) {
        levers.push({ label: 'At your current savings rate, that’s', value: `${months(monthsOfSaving)} of waiting` })
      }
    }
    levers.push({
      label: 'Or cut monthly spending during the break by',
      value: usd0(shortCash / Math.max(1, L)),
    })
  } else {
    levers.push({ label: 'You could stretch the break to', value: months(runwayMonths) })
    levers.push({
      label: 'Erase the retirement hit by saving this much more per month',
      tip: `Save this much every month between now and retirement and your balance at ${retirementAge} ends up where it would have been if you had never taken the break.`,
      value: usd0(
        monthlyRate > 0 && monthsToRetirement > 0
          ? longTermCost / ((Math.pow(1 + monthlyRate, monthsToRetirement) - 1) / monthlyRate)
          : longTermCost / Math.max(1, monthsToRetirement)
      ),
    })
  }
  const lastLever = levers.at(-1)
  if (lastLever) lastLever.emphasis = true

  return {
    coverage,
    verdict,
    verdictHeadline,
    verdictDetail,
    advisorHeadline,
    // The tool is built for the income band NWWP serves; below it the advisor CTA is noise.
    showAdvisorCard: income >= 100_000,

    breakNetMonthly,
    savingsMonthly,
    healthDelta,
    coverageNote,
    signedGapMonthly,
    totalNeed,
    reserve,
    available,
    projectedEndCash: Math.max(0, projectedEndCash),
    runwayMonths,
    taxNow: now.tax,
    taxDuringBreak: duringBreak.tax,

    missedTotal,
    missedFutureValue,
    drawdownFutureValue,
    longTermCost,
    projectedNoBreak,
    projectedWithBreak,
    payGap,
    socialSecurityReduced,
    socialSecurityNote,
    workYearsAfter,

    levers,
  }
}

/** Copy for the 401(k) contribution helper line, which depends on the 2026 deferral cap. */
export function contributionHint(state: CareerBreakState): string {
  const contributionRate = state.contributionPct / 100
  const annualDeferral = state.income * contributionRate
  const limit = deferralLimit(state.age)
  const catchUpLabel = state.age >= 60 && state.age <= 63 ? '60–63' : '50+'
  return (
    `${usd0(annualDeferral / 12)}/mo, or ${usd0(Math.min(annualDeferral, limit))}/yr` +
    (annualDeferral > limit ? ` — capped at your 2026 limit of ${usd0(limit)}` : '') +
    (limit > DEFERRAL.base ? ` (includes the age ${catchUpLabel} catch-up)` : '')
  )
}

/** Copy for the employer-match helper line. */
export function matchHint(state: CareerBreakState): string {
  const contributionRate = state.contributionPct / 100
  const matchRate = state.matchTenths / 1000
  const effectiveMatchRate = Math.min(matchRate, contributionRate)
  const annualMatch = state.income * effectiveMatchRate
  if (contributionRate <= 0) return 'You get no match while contributing 0%.'
  if (effectiveMatchRate < matchRate) {
    return `You’re contributing ${(contributionRate * 100).toFixed(0)}%, so you capture ${(effectiveMatchRate * 100).toFixed(1)}% of the ${(matchRate * 100).toFixed(1)}% available — ${usd0(annualMatch / 12)}/mo.`
  }
  return `Free money: ${usd0(annualMatch / 12)}/mo. U.S. average is 4.7% of pay.`
}
